/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  MigrationAssessmentArtifactClassificationPathParameters,
  MigrationAssessmentBuildIntegrationPathParameters,
  MigrationAssessmentBuildRunPathParameters,
  MigrationAssessmentConversionStatusPathParameters,
  MigrationAssessmentEnhancementStatusPathParameters,
  MigrationAssessmentEnhanceRunParams,
  MigrationAssessmentEnhanceRunPathParameters,
  MigrationAssessmentExtractionStatusParams,
  MigrationAssessmentFeasibilityMatrixPathParameters,
  MigrationAssessmentGetBuildResultPathParameters,
  MigrationAssessmentGetConversionItemPathParameters,
  MigrationAssessmentListBuildCandidatesPathParameters,
  MigrationAssessmentListIcosParams,
  MigrationAssessmentListNamespacesParams,
  MigrationAssessmentListRunsParams,
  MigrationAssessmentListSoftwareComponentsParams,
  MigrationAssessmentMigrationEstimatePathParameters,
  MigrationAssessmentMigrationPlanPathParameters,
  MigrationAssessmentMigrationReportParams,
  MigrationAssessmentMigrationReportPathParameters,
  MigrationAssessmentPrepareRunMappingsPathParameters,
  MigrationAssessmentPrepareWorkspacePathParameters,
  MigrationAssessmentReviewConversionItemPathParameters,
  MigrationAssessmentRunInventorySummaryPathParameters,
  MigrationAssessmentRunMappingsStatusPathParameters,
  MigrationAssessmentStartAiConversionParams,
  MigrationAssessmentStartAiConversionPathParameters,
  MigrationAssessmentStartConversionParams,
  MigrationAssessmentStartConversionPathParameters,
  MigrationAssessmentTestSinkChannelParams,
  MigrationAssessmentTestSinkChannelPathParameters,
  MigrationAssessmentTestSinkChannelsParams,
  MigrationAssessmentUploadRunMappingsPathParameters,
  MigrationAssessmentVerifyBuildPathParameters,
  TLArtifactClassificationResponse,
  TLBuildCandidatesResponse,
  TLBuildRequest,
  TLBuildResultResponse,
  TLConversionItemResponse,
  TLConversionReviewRequest,
  TLConversionRunStatusResponse,
  TLEnhancementJobResponse,
  TLExtractDirectRequest,
  TLExtractionStatusResponse,
  TLFeasibilityMatrixResponse,
  TLGenerateWorkspaceRequest,
  TLHTTPValidationError,
  TLIcosResponse,
  TLImportWorkspaceRequest,
  TLInventorySummaryResponse,
  TLMappingsStatusResponse,
  TLMigrationAssessmentProxyResponse,
  TLMigrationAssessmentUploadMappingsRequest,
  TLMigrationAssessmentUploadRunMappingsRequest,
  TLMigrationEstimateResponse,
  TLMigrationPlanResponse,
  TLNamespacesResponse,
  TLPaginatedResponseRunSummary,
  TLSoftwareComponentsResponse,
  TLSyncPoTenantRequest,
  TLSyncPoTenantResponse,
  TLTestSinkChannelResponse,
  TLTestSinkChannelsResponse,
  TLVerifyArtifactRequest,
  TLVerifyResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Migration Assessment Extract
 */
export const migrationAssessmentExtract = (
  tLExtractDirectRequestNull?: BodyType<TLExtractDirectRequest | null> | null,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLExtractionStatusResponse>(
    {
      url: `/v1/migration-assessment/extract`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLExtractDirectRequestNull,
      signal,
    },
    options,
  );
};

export const getMigrationAssessmentExtractQueryKey = (
  tLExtractDirectRequestNull?: BodyType<TLExtractDirectRequest | null>,
) => {
  return ['POST', 'v1', 'migration-assessment', 'extract', tLExtractDirectRequestNull] as const;
};

export const getMigrationAssessmentExtractQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentExtract>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExtractDirectRequestNull?: BodyType<TLExtractDirectRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtract>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentExtractQueryKey(tLExtractDirectRequestNull);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentExtract>>> = ({ signal }) =>
    migrationAssessmentExtract(tLExtractDirectRequestNull, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentExtract>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentExtractQueryResult = NonNullable<Awaited<ReturnType<typeof migrationAssessmentExtract>>>;
export type MigrationAssessmentExtractQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentExtract<
  TData = Awaited<ReturnType<typeof migrationAssessmentExtract>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExtractDirectRequestNull: undefined | BodyType<TLExtractDirectRequest | null>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtract>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentExtract>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentExtract>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentExtract<
  TData = Awaited<ReturnType<typeof migrationAssessmentExtract>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExtractDirectRequestNull?: BodyType<TLExtractDirectRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtract>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentExtract>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentExtract>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentExtract<
  TData = Awaited<ReturnType<typeof migrationAssessmentExtract>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExtractDirectRequestNull?: BodyType<TLExtractDirectRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtract>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Extract
 */

export function useMigrationAssessmentExtract<
  TData = Awaited<ReturnType<typeof migrationAssessmentExtract>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExtractDirectRequestNull?: BodyType<TLExtractDirectRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtract>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentExtractQueryOptions(tLExtractDirectRequestNull, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Extract
 */
export const prefetchMigrationAssessmentExtractQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentExtract>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLExtractDirectRequestNull?: BodyType<TLExtractDirectRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtract>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentExtractQueryOptions(tLExtractDirectRequestNull, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Extract
 */
export const invalidateMigrationAssessmentExtract = async (
  queryClient: QueryClient,
  tLExtractDirectRequestNull?: BodyType<TLExtractDirectRequest | null>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentExtractQueryKey(tLExtractDirectRequestNull) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Extraction Status
 */
export const migrationAssessmentExtractionStatus = (
  params?: MigrationAssessmentExtractionStatusParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLExtractionStatusResponse>(
    { url: `/v1/migration-assessment/extraction-status`, method: 'GET', params, signal },
    options,
  );
};

export const getMigrationAssessmentExtractionStatusQueryKey = (params?: MigrationAssessmentExtractionStatusParams) => {
  return ['v1', 'migration-assessment', 'extraction-status', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentExtractionStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentExtractionStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentExtractionStatusQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>> = ({ signal }) =>
    migrationAssessmentExtractionStatus(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentExtractionStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>
>;
export type MigrationAssessmentExtractionStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentExtractionStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | MigrationAssessmentExtractionStatusParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentExtractionStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentExtractionStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentExtractionStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentExtractionStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Extraction Status
 */

export function useMigrationAssessmentExtractionStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentExtractionStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentExtractionStatusQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Extraction Status
 */
export const prefetchMigrationAssessmentExtractionStatusQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: MigrationAssessmentExtractionStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentExtractionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentExtractionStatusQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Extraction Status
 */
export const invalidateMigrationAssessmentExtractionStatus = async (
  queryClient: QueryClient,
  params?: MigrationAssessmentExtractionStatusParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentExtractionStatusQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment Cancel Extraction
 */
export const migrationAssessmentCancelExtraction = (
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLExtractionStatusResponse>(
    { url: `/v1/migration-assessment/extraction/cancel`, method: 'POST', signal },
    options,
  );
};

export const getMigrationAssessmentCancelExtractionQueryKey = () => {
  return ['POST', 'v1', 'migration-assessment', 'extraction', 'cancel'] as const;
};

export const getMigrationAssessmentCancelExtractionQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentCancelExtractionQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>> = ({ signal }) =>
    migrationAssessmentCancelExtraction(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentCancelExtractionQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>
>;
export type MigrationAssessmentCancelExtractionQueryError = ErrorType<unknown>;

export function useMigrationAssessmentCancelExtraction<
  TData = Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentCancelExtraction<
  TData = Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentCancelExtraction<
  TData = Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Cancel Extraction
 */

export function useMigrationAssessmentCancelExtraction<
  TData = Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentCancelExtractionQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Cancel Extraction
 */
export const prefetchMigrationAssessmentCancelExtractionQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentCancelExtraction>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentCancelExtractionQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Cancel Extraction
 */
export const invalidateMigrationAssessmentCancelExtraction = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentCancelExtractionQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment List Icos
 */
export const migrationAssessmentListIcos = (
  params?: MigrationAssessmentListIcosParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIcosResponse>({ url: `/v1/migration-assessment/icos`, method: 'GET', params, signal }, options);
};

export const getMigrationAssessmentListIcosQueryKey = (params?: MigrationAssessmentListIcosParams) => {
  return ['v1', 'migration-assessment', 'icos', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentListIcosQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentListIcos>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListIcosParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListIcos>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentListIcosQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentListIcos>>> = ({ signal }) =>
    migrationAssessmentListIcos(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentListIcos>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentListIcosQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentListIcos>>
>;
export type MigrationAssessmentListIcosQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentListIcos<
  TData = Awaited<ReturnType<typeof migrationAssessmentListIcos>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | MigrationAssessmentListIcosParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListIcos>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListIcos>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListIcos>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListIcos<
  TData = Awaited<ReturnType<typeof migrationAssessmentListIcos>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListIcosParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListIcos>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListIcos>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListIcos>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListIcos<
  TData = Awaited<ReturnType<typeof migrationAssessmentListIcos>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListIcosParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListIcos>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment List Icos
 */

export function useMigrationAssessmentListIcos<
  TData = Awaited<ReturnType<typeof migrationAssessmentListIcos>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListIcosParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListIcos>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentListIcosQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment List Icos
 */
export const prefetchMigrationAssessmentListIcosQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentListIcos>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: MigrationAssessmentListIcosParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListIcos>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentListIcosQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment List Icos
 */
export const invalidateMigrationAssessmentListIcos = async (
  queryClient: QueryClient,
  params?: MigrationAssessmentListIcosParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentListIcosQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment Inventory Summary
 */
export const migrationAssessmentInventorySummary = (
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLInventorySummaryResponse>(
    { url: `/v1/migration-assessment/inventory-summary`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentInventorySummaryQueryKey = () => {
  return ['v1', 'migration-assessment', 'inventory-summary'] as const;
};

export const getMigrationAssessmentInventorySummaryQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentInventorySummaryQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>> = ({ signal }) =>
    migrationAssessmentInventorySummary(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentInventorySummaryQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>
>;
export type MigrationAssessmentInventorySummaryQueryError = ErrorType<unknown>;

export function useMigrationAssessmentInventorySummary<
  TData = Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentInventorySummary<
  TData = Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentInventorySummary<
  TData = Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Inventory Summary
 */

export function useMigrationAssessmentInventorySummary<
  TData = Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentInventorySummaryQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Inventory Summary
 */
export const prefetchMigrationAssessmentInventorySummaryQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentInventorySummary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentInventorySummaryQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Inventory Summary
 */
export const invalidateMigrationAssessmentInventorySummary = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentInventorySummaryQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment Upload Mappings
 */
export const migrationAssessmentUploadMappings = (
  tLMigrationAssessmentUploadMappingsRequest: BodyType<TLMigrationAssessmentUploadMappingsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append(`tpz_file`, tLMigrationAssessmentUploadMappingsRequest.tpz_file);

  return apiInstance<TLMigrationAssessmentProxyResponse>(
    {
      url: `/v1/migration-assessment/mappings/upload`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      signal,
    },
    options,
  );
};

export const getMigrationAssessmentUploadMappingsQueryKey = (
  tLMigrationAssessmentUploadMappingsRequest?: BodyType<TLMigrationAssessmentUploadMappingsRequest>,
) => {
  return [
    'POST',
    'v1',
    'migration-assessment',
    'mappings',
    'upload',
    tLMigrationAssessmentUploadMappingsRequest,
  ] as const;
};

export const getMigrationAssessmentUploadMappingsQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMigrationAssessmentUploadMappingsRequest: BodyType<TLMigrationAssessmentUploadMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getMigrationAssessmentUploadMappingsQueryKey(tLMigrationAssessmentUploadMappingsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>> = ({ signal }) =>
    migrationAssessmentUploadMappings(tLMigrationAssessmentUploadMappingsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentUploadMappingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>
>;
export type MigrationAssessmentUploadMappingsQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentUploadMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMigrationAssessmentUploadMappingsRequest: BodyType<TLMigrationAssessmentUploadMappingsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentUploadMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMigrationAssessmentUploadMappingsRequest: BodyType<TLMigrationAssessmentUploadMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentUploadMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMigrationAssessmentUploadMappingsRequest: BodyType<TLMigrationAssessmentUploadMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Upload Mappings
 */

export function useMigrationAssessmentUploadMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMigrationAssessmentUploadMappingsRequest: BodyType<TLMigrationAssessmentUploadMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentUploadMappingsQueryOptions(
    tLMigrationAssessmentUploadMappingsRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Upload Mappings
 */
export const prefetchMigrationAssessmentUploadMappingsQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLMigrationAssessmentUploadMappingsRequest: BodyType<TLMigrationAssessmentUploadMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentUploadMappingsQueryOptions(
    tLMigrationAssessmentUploadMappingsRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Upload Mappings
 */
export const invalidateMigrationAssessmentUploadMappings = async (
  queryClient: QueryClient,
  tLMigrationAssessmentUploadMappingsRequest: BodyType<TLMigrationAssessmentUploadMappingsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentUploadMappingsQueryKey(tLMigrationAssessmentUploadMappingsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment List Namespaces
 */
export const migrationAssessmentListNamespaces = (
  params?: MigrationAssessmentListNamespacesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNamespacesResponse>(
    { url: `/v1/migration-assessment/namespaces`, method: 'GET', params, signal },
    options,
  );
};

export const getMigrationAssessmentListNamespacesQueryKey = (params?: MigrationAssessmentListNamespacesParams) => {
  return ['v1', 'migration-assessment', 'namespaces', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentListNamespacesQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListNamespacesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentListNamespacesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>> = ({ signal }) =>
    migrationAssessmentListNamespaces(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentListNamespacesQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>
>;
export type MigrationAssessmentListNamespacesQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentListNamespaces<
  TData = Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | MigrationAssessmentListNamespacesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListNamespaces<
  TData = Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListNamespacesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListNamespaces<
  TData = Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListNamespacesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment List Namespaces
 */

export function useMigrationAssessmentListNamespaces<
  TData = Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListNamespacesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentListNamespacesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment List Namespaces
 */
export const prefetchMigrationAssessmentListNamespacesQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: MigrationAssessmentListNamespacesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListNamespaces>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentListNamespacesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment List Namespaces
 */
export const invalidateMigrationAssessmentListNamespaces = async (
  queryClient: QueryClient,
  params?: MigrationAssessmentListNamespacesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentListNamespacesQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment Sync Po Tenant
 */
export const migrationAssessmentSyncPoTenant = (
  tLSyncPoTenantRequestNull?: BodyType<TLSyncPoTenantRequest | null> | null,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSyncPoTenantResponse>(
    {
      url: `/v1/migration-assessment/po-tenant`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSyncPoTenantRequestNull,
      signal,
    },
    options,
  );
};

export const getMigrationAssessmentSyncPoTenantQueryKey = (
  tLSyncPoTenantRequestNull?: BodyType<TLSyncPoTenantRequest | null>,
) => {
  return ['POST', 'v1', 'migration-assessment', 'po-tenant', tLSyncPoTenantRequestNull] as const;
};

export const getMigrationAssessmentSyncPoTenantQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSyncPoTenantRequestNull?: BodyType<TLSyncPoTenantRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentSyncPoTenantQueryKey(tLSyncPoTenantRequestNull);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>> = ({ signal }) =>
    migrationAssessmentSyncPoTenant(tLSyncPoTenantRequestNull, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentSyncPoTenantQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>
>;
export type MigrationAssessmentSyncPoTenantQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentSyncPoTenant<
  TData = Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSyncPoTenantRequestNull: undefined | BodyType<TLSyncPoTenantRequest | null>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentSyncPoTenant<
  TData = Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSyncPoTenantRequestNull?: BodyType<TLSyncPoTenantRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentSyncPoTenant<
  TData = Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSyncPoTenantRequestNull?: BodyType<TLSyncPoTenantRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Sync Po Tenant
 */

export function useMigrationAssessmentSyncPoTenant<
  TData = Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSyncPoTenantRequestNull?: BodyType<TLSyncPoTenantRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentSyncPoTenantQueryOptions(tLSyncPoTenantRequestNull, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Sync Po Tenant
 */
export const prefetchMigrationAssessmentSyncPoTenantQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLSyncPoTenantRequestNull?: BodyType<TLSyncPoTenantRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentSyncPoTenant>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentSyncPoTenantQueryOptions(tLSyncPoTenantRequestNull, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Sync Po Tenant
 */
export const invalidateMigrationAssessmentSyncPoTenant = async (
  queryClient: QueryClient,
  tLSyncPoTenantRequestNull?: BodyType<TLSyncPoTenantRequest | null>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentSyncPoTenantQueryKey(tLSyncPoTenantRequestNull) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment List Runs
 */
export const migrationAssessmentListRuns = (
  params?: MigrationAssessmentListRunsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseRunSummary>(
    { url: `/v1/migration-assessment/runs`, method: 'GET', params, signal },
    options,
  );
};

export const getMigrationAssessmentListRunsQueryKey = (params?: MigrationAssessmentListRunsParams) => {
  return ['v1', 'migration-assessment', 'runs', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentListRunsQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentListRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentListRunsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentListRuns>>> = ({ signal }) =>
    migrationAssessmentListRuns(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentListRuns>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentListRunsQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentListRuns>>
>;
export type MigrationAssessmentListRunsQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentListRuns<
  TData = Awaited<ReturnType<typeof migrationAssessmentListRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | MigrationAssessmentListRunsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListRuns>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListRuns>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListRuns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListRuns<
  TData = Awaited<ReturnType<typeof migrationAssessmentListRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListRuns>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListRuns>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListRuns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListRuns<
  TData = Awaited<ReturnType<typeof migrationAssessmentListRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment List Runs
 */

export function useMigrationAssessmentListRuns<
  TData = Awaited<ReturnType<typeof migrationAssessmentListRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentListRunsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment List Runs
 */
export const prefetchMigrationAssessmentListRunsQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentListRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: MigrationAssessmentListRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentListRunsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment List Runs
 */
export const invalidateMigrationAssessmentListRuns = async (
  queryClient: QueryClient,
  params?: MigrationAssessmentListRunsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentListRunsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment Artifact Classification
 */
export const migrationAssessmentArtifactClassification = (
  { runId }: MigrationAssessmentArtifactClassificationPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLArtifactClassificationResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/artifact-classification`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentArtifactClassificationQueryKey = ({
  runId,
}: MigrationAssessmentArtifactClassificationPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'artifact-classification'] as const;
};

export const getMigrationAssessmentArtifactClassificationQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentArtifactClassificationPathParameters,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentArtifactClassificationQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>> = ({ signal }) =>
    migrationAssessmentArtifactClassification({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentArtifactClassificationQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>
>;
export type MigrationAssessmentArtifactClassificationQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentArtifactClassification<
  TData = Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentArtifactClassificationPathParameters,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentArtifactClassification<
  TData = Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentArtifactClassificationPathParameters,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentArtifactClassification<
  TData = Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentArtifactClassificationPathParameters,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Artifact Classification
 */

export function useMigrationAssessmentArtifactClassification<
  TData = Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentArtifactClassificationPathParameters,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentArtifactClassificationQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Artifact Classification
 */
export const prefetchMigrationAssessmentArtifactClassificationQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentArtifactClassificationPathParameters,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentArtifactClassification>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentArtifactClassificationQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Artifact Classification
 */
export const invalidateMigrationAssessmentArtifactClassification = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentArtifactClassificationPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentArtifactClassificationQueryKey({ runId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Build Run
 */
export const migrationAssessmentBuildRun = (
  { runId }: MigrationAssessmentBuildRunPathParameters,
  tLBuildRequestNull?: BodyType<TLBuildRequest | null> | null,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBuildResultResponse>(
    {
      url: `/v1/migration-assessment/runs/${runId}/build`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLBuildRequestNull,
      signal,
    },
    options,
  );
};

export const getMigrationAssessmentBuildRunQueryKey = (
  { runId }: MigrationAssessmentBuildRunPathParameters,
  tLBuildRequestNull?: BodyType<TLBuildRequest | null>,
) => {
  return ['POST', 'v1', 'migration-assessment', 'runs', runId, 'build', tLBuildRequestNull] as const;
};

export const getMigrationAssessmentBuildRunQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentBuildRunPathParameters,
  tLBuildRequestNull?: BodyType<TLBuildRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentBuildRunQueryKey({ runId }, tLBuildRequestNull);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentBuildRun>>> = ({ signal }) =>
    migrationAssessmentBuildRun({ runId }, tLBuildRequestNull, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentBuildRun>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentBuildRunQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentBuildRun>>
>;
export type MigrationAssessmentBuildRunQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentBuildRun<
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentBuildRunPathParameters,
  tLBuildRequestNull: undefined | BodyType<TLBuildRequest | null>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildRun>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentBuildRun>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentBuildRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentBuildRun<
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentBuildRunPathParameters,
  tLBuildRequestNull?: BodyType<TLBuildRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildRun>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentBuildRun>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentBuildRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentBuildRun<
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentBuildRunPathParameters,
  tLBuildRequestNull?: BodyType<TLBuildRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Build Run
 */

export function useMigrationAssessmentBuildRun<
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentBuildRunPathParameters,
  tLBuildRequestNull?: BodyType<TLBuildRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentBuildRunQueryOptions({ runId }, tLBuildRequestNull, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Build Run
 */
export const prefetchMigrationAssessmentBuildRunQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentBuildRunPathParameters,
  tLBuildRequestNull?: BodyType<TLBuildRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentBuildRunQueryOptions({ runId }, tLBuildRequestNull, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Build Run
 */
export const invalidateMigrationAssessmentBuildRun = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentBuildRunPathParameters,
  tLBuildRequestNull?: BodyType<TLBuildRequest | null>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentBuildRunQueryKey({ runId }, tLBuildRequestNull) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment List Build Candidates
 */
export const migrationAssessmentListBuildCandidates = (
  { runId }: MigrationAssessmentListBuildCandidatesPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBuildCandidatesResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/build-candidates`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentListBuildCandidatesQueryKey = ({
  runId,
}: MigrationAssessmentListBuildCandidatesPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'build-candidates'] as const;
};

export const getMigrationAssessmentListBuildCandidatesQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentListBuildCandidatesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentListBuildCandidatesQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>> = ({ signal }) =>
    migrationAssessmentListBuildCandidates({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentListBuildCandidatesQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>
>;
export type MigrationAssessmentListBuildCandidatesQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentListBuildCandidates<
  TData = Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentListBuildCandidatesPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListBuildCandidates<
  TData = Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentListBuildCandidatesPathParameters,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListBuildCandidates<
  TData = Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentListBuildCandidatesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment List Build Candidates
 */

export function useMigrationAssessmentListBuildCandidates<
  TData = Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentListBuildCandidatesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentListBuildCandidatesQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment List Build Candidates
 */
export const prefetchMigrationAssessmentListBuildCandidatesQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentListBuildCandidatesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListBuildCandidates>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentListBuildCandidatesQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment List Build Candidates
 */
export const invalidateMigrationAssessmentListBuildCandidates = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentListBuildCandidatesPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentListBuildCandidatesQueryKey({ runId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Get Build Result
 */
export const migrationAssessmentGetBuildResult = (
  { runId }: MigrationAssessmentGetBuildResultPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBuildResultResponse | null>(
    { url: `/v1/migration-assessment/runs/${runId}/build-result`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentGetBuildResultQueryKey = ({
  runId,
}: MigrationAssessmentGetBuildResultPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'build-result'] as const;
};

export const getMigrationAssessmentGetBuildResultQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentGetBuildResultPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentGetBuildResultQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>> = ({ signal }) =>
    migrationAssessmentGetBuildResult({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentGetBuildResultQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>
>;
export type MigrationAssessmentGetBuildResultQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentGetBuildResult<
  TData = Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentGetBuildResultPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentGetBuildResult<
  TData = Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentGetBuildResultPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentGetBuildResult<
  TData = Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentGetBuildResultPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Get Build Result
 */

export function useMigrationAssessmentGetBuildResult<
  TData = Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentGetBuildResultPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentGetBuildResultQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Get Build Result
 */
export const prefetchMigrationAssessmentGetBuildResultQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentGetBuildResultPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetBuildResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentGetBuildResultQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Get Build Result
 */
export const invalidateMigrationAssessmentGetBuildResult = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentGetBuildResultPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentGetBuildResultQueryKey({ runId }) }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment Conversion Status
 */
export const migrationAssessmentConversionStatus = (
  { runId }: MigrationAssessmentConversionStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversionRunStatusResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/convert`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentConversionStatusQueryKey = ({
  runId,
}: MigrationAssessmentConversionStatusPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'convert'] as const;
};

export const getMigrationAssessmentConversionStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentConversionStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentConversionStatusQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>> = ({ signal }) =>
    migrationAssessmentConversionStatus({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentConversionStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>
>;
export type MigrationAssessmentConversionStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentConversionStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentConversionStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentConversionStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentConversionStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentConversionStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentConversionStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Conversion Status
 */

export function useMigrationAssessmentConversionStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentConversionStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentConversionStatusQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Conversion Status
 */
export const prefetchMigrationAssessmentConversionStatusQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentConversionStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentConversionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentConversionStatusQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Conversion Status
 */
export const invalidateMigrationAssessmentConversionStatus = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentConversionStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentConversionStatusQueryKey({ runId }) }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment Start Conversion
 */
export const migrationAssessmentStartConversion = (
  { runId }: MigrationAssessmentStartConversionPathParameters,
  params?: MigrationAssessmentStartConversionParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversionRunStatusResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/convert`, method: 'POST', params, signal },
    options,
  );
};

export const getMigrationAssessmentStartConversionQueryKey = (
  { runId }: MigrationAssessmentStartConversionPathParameters,
  params?: MigrationAssessmentStartConversionParams,
) => {
  return ['POST', 'v1', 'migration-assessment', 'runs', runId, 'convert', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentStartConversionQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentStartConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentStartConversionPathParameters,
  params?: MigrationAssessmentStartConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartConversion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentStartConversionQueryKey({ runId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentStartConversion>>> = ({ signal }) =>
    migrationAssessmentStartConversion({ runId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentStartConversion>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentStartConversionQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentStartConversion>>
>;
export type MigrationAssessmentStartConversionQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentStartConversion<
  TData = Awaited<ReturnType<typeof migrationAssessmentStartConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentStartConversionPathParameters,
  params: undefined | MigrationAssessmentStartConversionParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartConversion>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentStartConversion>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentStartConversion>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentStartConversion<
  TData = Awaited<ReturnType<typeof migrationAssessmentStartConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentStartConversionPathParameters,
  params?: MigrationAssessmentStartConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartConversion>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentStartConversion>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentStartConversion>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentStartConversion<
  TData = Awaited<ReturnType<typeof migrationAssessmentStartConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentStartConversionPathParameters,
  params?: MigrationAssessmentStartConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartConversion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Start Conversion
 */

export function useMigrationAssessmentStartConversion<
  TData = Awaited<ReturnType<typeof migrationAssessmentStartConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentStartConversionPathParameters,
  params?: MigrationAssessmentStartConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartConversion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentStartConversionQueryOptions({ runId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Start Conversion
 */
export const prefetchMigrationAssessmentStartConversionQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentStartConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentStartConversionPathParameters,
  params?: MigrationAssessmentStartConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartConversion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentStartConversionQueryOptions({ runId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Start Conversion
 */
export const invalidateMigrationAssessmentStartConversion = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentStartConversionPathParameters,
  params?: MigrationAssessmentStartConversionParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentStartConversionQueryKey({ runId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Start Ai Conversion
 */
export const migrationAssessmentStartAiConversion = (
  { runId }: MigrationAssessmentStartAiConversionPathParameters,
  params?: MigrationAssessmentStartAiConversionParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversionRunStatusResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/convert/ai`, method: 'POST', params, signal },
    options,
  );
};

export const getMigrationAssessmentStartAiConversionQueryKey = (
  { runId }: MigrationAssessmentStartAiConversionPathParameters,
  params?: MigrationAssessmentStartAiConversionParams,
) => {
  return ['POST', 'v1', 'migration-assessment', 'runs', runId, 'convert', 'ai', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentStartAiConversionQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentStartAiConversionPathParameters,
  params?: MigrationAssessmentStartAiConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentStartAiConversionQueryKey({ runId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>> = ({ signal }) =>
    migrationAssessmentStartAiConversion({ runId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentStartAiConversionQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>
>;
export type MigrationAssessmentStartAiConversionQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentStartAiConversion<
  TData = Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentStartAiConversionPathParameters,
  params: undefined | MigrationAssessmentStartAiConversionParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentStartAiConversion<
  TData = Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentStartAiConversionPathParameters,
  params?: MigrationAssessmentStartAiConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentStartAiConversion<
  TData = Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentStartAiConversionPathParameters,
  params?: MigrationAssessmentStartAiConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Start Ai Conversion
 */

export function useMigrationAssessmentStartAiConversion<
  TData = Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentStartAiConversionPathParameters,
  params?: MigrationAssessmentStartAiConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentStartAiConversionQueryOptions({ runId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Start Ai Conversion
 */
export const prefetchMigrationAssessmentStartAiConversionQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentStartAiConversionPathParameters,
  params?: MigrationAssessmentStartAiConversionParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentStartAiConversion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentStartAiConversionQueryOptions({ runId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Start Ai Conversion
 */
export const invalidateMigrationAssessmentStartAiConversion = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentStartAiConversionPathParameters,
  params?: MigrationAssessmentStartAiConversionParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentStartAiConversionQueryKey({ runId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Get Conversion Item
 */
export const migrationAssessmentGetConversionItem = (
  { runId, conversionItemId }: MigrationAssessmentGetConversionItemPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversionItemResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/convert/items/${conversionItemId}`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentGetConversionItemQueryKey = ({
  runId,
  conversionItemId,
}: MigrationAssessmentGetConversionItemPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'convert', 'items', conversionItemId] as const;
};

export const getMigrationAssessmentGetConversionItemQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId, conversionItemId }: MigrationAssessmentGetConversionItemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getMigrationAssessmentGetConversionItemQueryKey({ runId, conversionItemId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>> = ({ signal }) =>
    migrationAssessmentGetConversionItem({ runId, conversionItemId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: runId !== null && runId !== undefined && conversionItemId !== null && conversionItemId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type MigrationAssessmentGetConversionItemQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>
>;
export type MigrationAssessmentGetConversionItemQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentGetConversionItem<
  TData = Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentGetConversionItemPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentGetConversionItem<
  TData = Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentGetConversionItemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentGetConversionItem<
  TData = Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentGetConversionItemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Get Conversion Item
 */

export function useMigrationAssessmentGetConversionItem<
  TData = Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId, conversionItemId }: MigrationAssessmentGetConversionItemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentGetConversionItemQueryOptions({ runId, conversionItemId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Get Conversion Item
 */
export const prefetchMigrationAssessmentGetConversionItemQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId, conversionItemId }: MigrationAssessmentGetConversionItemPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentGetConversionItem>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentGetConversionItemQueryOptions({ runId, conversionItemId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Get Conversion Item
 */
export const invalidateMigrationAssessmentGetConversionItem = async (
  queryClient: QueryClient,
  { runId, conversionItemId }: MigrationAssessmentGetConversionItemPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentGetConversionItemQueryKey({ runId, conversionItemId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Review Conversion Item
 */
export const migrationAssessmentReviewConversionItem = (
  { runId, conversionItemId }: MigrationAssessmentReviewConversionItemPathParameters,
  tLConversionReviewRequest: BodyType<TLConversionReviewRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversionItemResponse>(
    {
      url: `/v1/migration-assessment/runs/${runId}/convert/items/${conversionItemId}/review`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLConversionReviewRequest,
      signal,
    },
    options,
  );
};

export const getMigrationAssessmentReviewConversionItemQueryKey = (
  { runId, conversionItemId }: MigrationAssessmentReviewConversionItemPathParameters,
  tLConversionReviewRequest?: BodyType<TLConversionReviewRequest>,
) => {
  return [
    'POST',
    'v1',
    'migration-assessment',
    'runs',
    runId,
    'convert',
    'items',
    conversionItemId,
    'review',
    tLConversionReviewRequest,
  ] as const;
};

export const getMigrationAssessmentReviewConversionItemQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId, conversionItemId }: MigrationAssessmentReviewConversionItemPathParameters,
  tLConversionReviewRequest: BodyType<TLConversionReviewRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getMigrationAssessmentReviewConversionItemQueryKey({ runId, conversionItemId }, tLConversionReviewRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>> = ({ signal }) =>
    migrationAssessmentReviewConversionItem(
      { runId, conversionItemId },
      tLConversionReviewRequest,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: runId !== null && runId !== undefined && conversionItemId !== null && conversionItemId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type MigrationAssessmentReviewConversionItemQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>
>;
export type MigrationAssessmentReviewConversionItemQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentReviewConversionItem<
  TData = Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentReviewConversionItemPathParameters,
  tLConversionReviewRequest: BodyType<TLConversionReviewRequest>,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentReviewConversionItem<
  TData = Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentReviewConversionItemPathParameters,
  tLConversionReviewRequest: BodyType<TLConversionReviewRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentReviewConversionItem<
  TData = Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentReviewConversionItemPathParameters,
  tLConversionReviewRequest: BodyType<TLConversionReviewRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Review Conversion Item
 */

export function useMigrationAssessmentReviewConversionItem<
  TData = Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId, conversionItemId }: MigrationAssessmentReviewConversionItemPathParameters,
  tLConversionReviewRequest: BodyType<TLConversionReviewRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentReviewConversionItemQueryOptions(
    { runId, conversionItemId },
    tLConversionReviewRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Review Conversion Item
 */
export const prefetchMigrationAssessmentReviewConversionItemQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId, conversionItemId }: MigrationAssessmentReviewConversionItemPathParameters,
  tLConversionReviewRequest: BodyType<TLConversionReviewRequest>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentReviewConversionItem>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentReviewConversionItemQueryOptions(
    { runId, conversionItemId },
    tLConversionReviewRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Review Conversion Item
 */
export const invalidateMigrationAssessmentReviewConversionItem = async (
  queryClient: QueryClient,
  { runId, conversionItemId }: MigrationAssessmentReviewConversionItemPathParameters,
  tLConversionReviewRequest: BodyType<TLConversionReviewRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getMigrationAssessmentReviewConversionItemQueryKey(
        { runId, conversionItemId },
        tLConversionReviewRequest,
      ),
    },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Enhancement Status
 */
export const migrationAssessmentEnhancementStatus = (
  { runId }: MigrationAssessmentEnhancementStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLEnhancementJobResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/enhance`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentEnhancementStatusQueryKey = ({
  runId,
}: MigrationAssessmentEnhancementStatusPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'enhance'] as const;
};

export const getMigrationAssessmentEnhancementStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentEnhancementStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentEnhancementStatusQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>> = ({ signal }) =>
    migrationAssessmentEnhancementStatus({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentEnhancementStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>
>;
export type MigrationAssessmentEnhancementStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentEnhancementStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentEnhancementStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentEnhancementStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentEnhancementStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentEnhancementStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentEnhancementStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Enhancement Status
 */

export function useMigrationAssessmentEnhancementStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentEnhancementStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentEnhancementStatusQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Enhancement Status
 */
export const prefetchMigrationAssessmentEnhancementStatusQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentEnhancementStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhancementStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentEnhancementStatusQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Enhancement Status
 */
export const invalidateMigrationAssessmentEnhancementStatus = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentEnhancementStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentEnhancementStatusQueryKey({ runId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Enhance Run
 */
export const migrationAssessmentEnhanceRun = (
  { runId }: MigrationAssessmentEnhanceRunPathParameters,
  params?: MigrationAssessmentEnhanceRunParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLEnhancementJobResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/enhance`, method: 'POST', params, signal },
    options,
  );
};

export const getMigrationAssessmentEnhanceRunQueryKey = (
  { runId }: MigrationAssessmentEnhanceRunPathParameters,
  params?: MigrationAssessmentEnhanceRunParams,
) => {
  return ['POST', 'v1', 'migration-assessment', 'runs', runId, 'enhance', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentEnhanceRunQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentEnhanceRunPathParameters,
  params?: MigrationAssessmentEnhanceRunParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentEnhanceRunQueryKey({ runId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>> = ({ signal }) =>
    migrationAssessmentEnhanceRun({ runId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentEnhanceRunQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>
>;
export type MigrationAssessmentEnhanceRunQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentEnhanceRun<
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentEnhanceRunPathParameters,
  params: undefined | MigrationAssessmentEnhanceRunParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentEnhanceRun<
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentEnhanceRunPathParameters,
  params?: MigrationAssessmentEnhanceRunParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentEnhanceRun<
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentEnhanceRunPathParameters,
  params?: MigrationAssessmentEnhanceRunParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Enhance Run
 */

export function useMigrationAssessmentEnhanceRun<
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentEnhanceRunPathParameters,
  params?: MigrationAssessmentEnhanceRunParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentEnhanceRunQueryOptions({ runId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Enhance Run
 */
export const prefetchMigrationAssessmentEnhanceRunQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentEnhanceRunPathParameters,
  params?: MigrationAssessmentEnhanceRunParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentEnhanceRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentEnhanceRunQueryOptions({ runId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Enhance Run
 */
export const invalidateMigrationAssessmentEnhanceRun = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentEnhanceRunPathParameters,
  params?: MigrationAssessmentEnhanceRunParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentEnhanceRunQueryKey({ runId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Feasibility Matrix
 */
export const migrationAssessmentFeasibilityMatrix = (
  { runId }: MigrationAssessmentFeasibilityMatrixPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFeasibilityMatrixResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/feasibility-matrix`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentFeasibilityMatrixQueryKey = ({
  runId,
}: MigrationAssessmentFeasibilityMatrixPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'feasibility-matrix'] as const;
};

export const getMigrationAssessmentFeasibilityMatrixQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentFeasibilityMatrixPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentFeasibilityMatrixQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>> = ({ signal }) =>
    migrationAssessmentFeasibilityMatrix({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentFeasibilityMatrixQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>
>;
export type MigrationAssessmentFeasibilityMatrixQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentFeasibilityMatrix<
  TData = Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentFeasibilityMatrixPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentFeasibilityMatrix<
  TData = Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentFeasibilityMatrixPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentFeasibilityMatrix<
  TData = Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentFeasibilityMatrixPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Feasibility Matrix
 */

export function useMigrationAssessmentFeasibilityMatrix<
  TData = Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentFeasibilityMatrixPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentFeasibilityMatrixQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Feasibility Matrix
 */
export const prefetchMigrationAssessmentFeasibilityMatrixQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentFeasibilityMatrixPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentFeasibilityMatrix>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentFeasibilityMatrixQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Feasibility Matrix
 */
export const invalidateMigrationAssessmentFeasibilityMatrix = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentFeasibilityMatrixPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentFeasibilityMatrixQueryKey({ runId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Run Inventory Summary
 */
export const migrationAssessmentRunInventorySummary = (
  { runId }: MigrationAssessmentRunInventorySummaryPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLInventorySummaryResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/inventory-summary`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentRunInventorySummaryQueryKey = ({
  runId,
}: MigrationAssessmentRunInventorySummaryPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'inventory-summary'] as const;
};

export const getMigrationAssessmentRunInventorySummaryQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentRunInventorySummaryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentRunInventorySummaryQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>> = ({ signal }) =>
    migrationAssessmentRunInventorySummary({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentRunInventorySummaryQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>
>;
export type MigrationAssessmentRunInventorySummaryQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentRunInventorySummary<
  TData = Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentRunInventorySummaryPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentRunInventorySummary<
  TData = Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentRunInventorySummaryPathParameters,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentRunInventorySummary<
  TData = Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentRunInventorySummaryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Run Inventory Summary
 */

export function useMigrationAssessmentRunInventorySummary<
  TData = Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentRunInventorySummaryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentRunInventorySummaryQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Run Inventory Summary
 */
export const prefetchMigrationAssessmentRunInventorySummaryQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentRunInventorySummaryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunInventorySummary>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentRunInventorySummaryQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Run Inventory Summary
 */
export const invalidateMigrationAssessmentRunInventorySummary = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentRunInventorySummaryPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentRunInventorySummaryQueryKey({ runId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Run Mappings Status
 */
export const migrationAssessmentRunMappingsStatus = (
  { runId }: MigrationAssessmentRunMappingsStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMappingsStatusResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/mappings`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentRunMappingsStatusQueryKey = ({
  runId,
}: MigrationAssessmentRunMappingsStatusPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'mappings'] as const;
};

export const getMigrationAssessmentRunMappingsStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentRunMappingsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentRunMappingsStatusQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>> = ({ signal }) =>
    migrationAssessmentRunMappingsStatus({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentRunMappingsStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>
>;
export type MigrationAssessmentRunMappingsStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentRunMappingsStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentRunMappingsStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentRunMappingsStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentRunMappingsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentRunMappingsStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentRunMappingsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Run Mappings Status
 */

export function useMigrationAssessmentRunMappingsStatus<
  TData = Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentRunMappingsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentRunMappingsStatusQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Run Mappings Status
 */
export const prefetchMigrationAssessmentRunMappingsStatusQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentRunMappingsStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentRunMappingsStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentRunMappingsStatusQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Run Mappings Status
 */
export const invalidateMigrationAssessmentRunMappingsStatus = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentRunMappingsStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentRunMappingsStatusQueryKey({ runId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Prepare Run Mappings
 */
export const migrationAssessmentPrepareRunMappings = (
  { runId }: MigrationAssessmentPrepareRunMappingsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMappingsStatusResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/mappings/prepare`, method: 'POST', signal },
    options,
  );
};

export const getMigrationAssessmentPrepareRunMappingsQueryKey = ({
  runId,
}: MigrationAssessmentPrepareRunMappingsPathParameters) => {
  return ['POST', 'v1', 'migration-assessment', 'runs', runId, 'mappings', 'prepare'] as const;
};

export const getMigrationAssessmentPrepareRunMappingsQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentPrepareRunMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentPrepareRunMappingsQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>> = ({ signal }) =>
    migrationAssessmentPrepareRunMappings({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentPrepareRunMappingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>
>;
export type MigrationAssessmentPrepareRunMappingsQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentPrepareRunMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentPrepareRunMappingsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentPrepareRunMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentPrepareRunMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentPrepareRunMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentPrepareRunMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Prepare Run Mappings
 */

export function useMigrationAssessmentPrepareRunMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentPrepareRunMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentPrepareRunMappingsQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Prepare Run Mappings
 */
export const prefetchMigrationAssessmentPrepareRunMappingsQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentPrepareRunMappingsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareRunMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentPrepareRunMappingsQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Prepare Run Mappings
 */
export const invalidateMigrationAssessmentPrepareRunMappings = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentPrepareRunMappingsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentPrepareRunMappingsQueryKey({ runId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Upload Run Mappings
 */
export const migrationAssessmentUploadRunMappings = (
  { runId }: MigrationAssessmentUploadRunMappingsPathParameters,
  tLMigrationAssessmentUploadRunMappingsRequest: BodyType<TLMigrationAssessmentUploadRunMappingsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append(`tpz_file`, tLMigrationAssessmentUploadRunMappingsRequest.tpz_file);

  return apiInstance<TLMappingsStatusResponse>(
    {
      url: `/v1/migration-assessment/runs/${runId}/mappings/upload`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      signal,
    },
    options,
  );
};

export const getMigrationAssessmentUploadRunMappingsQueryKey = (
  { runId }: MigrationAssessmentUploadRunMappingsPathParameters,
  tLMigrationAssessmentUploadRunMappingsRequest?: BodyType<TLMigrationAssessmentUploadRunMappingsRequest>,
) => {
  return [
    'POST',
    'v1',
    'migration-assessment',
    'runs',
    runId,
    'mappings',
    'upload',
    tLMigrationAssessmentUploadRunMappingsRequest,
  ] as const;
};

export const getMigrationAssessmentUploadRunMappingsQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentUploadRunMappingsPathParameters,
  tLMigrationAssessmentUploadRunMappingsRequest: BodyType<TLMigrationAssessmentUploadRunMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getMigrationAssessmentUploadRunMappingsQueryKey({ runId }, tLMigrationAssessmentUploadRunMappingsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>> = ({ signal }) =>
    migrationAssessmentUploadRunMappings(
      { runId },
      tLMigrationAssessmentUploadRunMappingsRequest,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentUploadRunMappingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>
>;
export type MigrationAssessmentUploadRunMappingsQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentUploadRunMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentUploadRunMappingsPathParameters,
  tLMigrationAssessmentUploadRunMappingsRequest: BodyType<TLMigrationAssessmentUploadRunMappingsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentUploadRunMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentUploadRunMappingsPathParameters,
  tLMigrationAssessmentUploadRunMappingsRequest: BodyType<TLMigrationAssessmentUploadRunMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentUploadRunMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentUploadRunMappingsPathParameters,
  tLMigrationAssessmentUploadRunMappingsRequest: BodyType<TLMigrationAssessmentUploadRunMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Upload Run Mappings
 */

export function useMigrationAssessmentUploadRunMappings<
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentUploadRunMappingsPathParameters,
  tLMigrationAssessmentUploadRunMappingsRequest: BodyType<TLMigrationAssessmentUploadRunMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentUploadRunMappingsQueryOptions(
    { runId },
    tLMigrationAssessmentUploadRunMappingsRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Upload Run Mappings
 */
export const prefetchMigrationAssessmentUploadRunMappingsQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentUploadRunMappingsPathParameters,
  tLMigrationAssessmentUploadRunMappingsRequest: BodyType<TLMigrationAssessmentUploadRunMappingsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentUploadRunMappings>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentUploadRunMappingsQueryOptions(
    { runId },
    tLMigrationAssessmentUploadRunMappingsRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Upload Run Mappings
 */
export const invalidateMigrationAssessmentUploadRunMappings = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentUploadRunMappingsPathParameters,
  tLMigrationAssessmentUploadRunMappingsRequest: BodyType<TLMigrationAssessmentUploadRunMappingsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getMigrationAssessmentUploadRunMappingsQueryKey(
        { runId },
        tLMigrationAssessmentUploadRunMappingsRequest,
      ),
    },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Migration Estimate
 */
export const migrationAssessmentMigrationEstimate = (
  { runId }: MigrationAssessmentMigrationEstimatePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMigrationEstimateResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/migration-estimate`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentMigrationEstimateQueryKey = ({
  runId,
}: MigrationAssessmentMigrationEstimatePathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'migration-estimate'] as const;
};

export const getMigrationAssessmentMigrationEstimateQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentMigrationEstimatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentMigrationEstimateQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>> = ({ signal }) =>
    migrationAssessmentMigrationEstimate({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentMigrationEstimateQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>
>;
export type MigrationAssessmentMigrationEstimateQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentMigrationEstimate<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentMigrationEstimatePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentMigrationEstimate<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentMigrationEstimatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentMigrationEstimate<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentMigrationEstimatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Migration Estimate
 */

export function useMigrationAssessmentMigrationEstimate<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentMigrationEstimatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentMigrationEstimateQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Migration Estimate
 */
export const prefetchMigrationAssessmentMigrationEstimateQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentMigrationEstimatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationEstimate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentMigrationEstimateQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Migration Estimate
 */
export const invalidateMigrationAssessmentMigrationEstimate = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentMigrationEstimatePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentMigrationEstimateQueryKey({ runId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Migration Plan
 */
export const migrationAssessmentMigrationPlan = (
  { runId }: MigrationAssessmentMigrationPlanPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMigrationPlanResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/migration-plan`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentMigrationPlanQueryKey = ({
  runId,
}: MigrationAssessmentMigrationPlanPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'migration-plan'] as const;
};

export const getMigrationAssessmentMigrationPlanQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentMigrationPlanPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentMigrationPlanQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>> = ({ signal }) =>
    migrationAssessmentMigrationPlan({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentMigrationPlanQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>
>;
export type MigrationAssessmentMigrationPlanQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentMigrationPlan<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentMigrationPlanPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentMigrationPlan<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentMigrationPlanPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentMigrationPlan<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentMigrationPlanPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Migration Plan
 */

export function useMigrationAssessmentMigrationPlan<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentMigrationPlanPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentMigrationPlanQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Migration Plan
 */
export const prefetchMigrationAssessmentMigrationPlanQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentMigrationPlanPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationPlan>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentMigrationPlanQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Migration Plan
 */
export const invalidateMigrationAssessmentMigrationPlan = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentMigrationPlanPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentMigrationPlanQueryKey({ runId }) }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment Migration Report
 */
export const migrationAssessmentMigrationReport = (
  { runId }: MigrationAssessmentMigrationReportPathParameters,
  params?: MigrationAssessmentMigrationReportParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>(
    { url: `/v1/migration-assessment/runs/${runId}/migration-report`, method: 'GET', params, signal },
    options,
  );
};

export const getMigrationAssessmentMigrationReportQueryKey = (
  { runId }: MigrationAssessmentMigrationReportPathParameters,
  params?: MigrationAssessmentMigrationReportParams,
) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'migration-report', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentMigrationReportQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentMigrationReportPathParameters,
  params?: MigrationAssessmentMigrationReportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentMigrationReportQueryKey({ runId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>> = ({ signal }) =>
    migrationAssessmentMigrationReport({ runId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentMigrationReportQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>
>;
export type MigrationAssessmentMigrationReportQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentMigrationReport<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentMigrationReportPathParameters,
  params: undefined | MigrationAssessmentMigrationReportParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentMigrationReport<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentMigrationReportPathParameters,
  params?: MigrationAssessmentMigrationReportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentMigrationReport<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentMigrationReportPathParameters,
  params?: MigrationAssessmentMigrationReportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Migration Report
 */

export function useMigrationAssessmentMigrationReport<
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentMigrationReportPathParameters,
  params?: MigrationAssessmentMigrationReportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentMigrationReportQueryOptions({ runId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Migration Report
 */
export const prefetchMigrationAssessmentMigrationReportQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentMigrationReportPathParameters,
  params?: MigrationAssessmentMigrationReportParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentMigrationReport>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentMigrationReportQueryOptions({ runId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Migration Report
 */
export const invalidateMigrationAssessmentMigrationReport = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentMigrationReportPathParameters,
  params?: MigrationAssessmentMigrationReportParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentMigrationReportQueryKey({ runId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Verify Build
 */
export const migrationAssessmentVerifyBuild = (
  { runId }: MigrationAssessmentVerifyBuildPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLVerifyResponse>(
    { url: `/v1/migration-assessment/runs/${runId}/verify`, method: 'GET', signal },
    options,
  );
};

export const getMigrationAssessmentVerifyBuildQueryKey = ({ runId }: MigrationAssessmentVerifyBuildPathParameters) => {
  return ['v1', 'migration-assessment', 'runs', runId, 'verify'] as const;
};

export const getMigrationAssessmentVerifyBuildQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentVerifyBuildPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentVerifyBuildQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>> = ({ signal }) =>
    migrationAssessmentVerifyBuild({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentVerifyBuildQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>
>;
export type MigrationAssessmentVerifyBuildQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentVerifyBuild<
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentVerifyBuildPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentVerifyBuild<
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentVerifyBuildPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentVerifyBuild<
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentVerifyBuildPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Verify Build
 */

export function useMigrationAssessmentVerifyBuild<
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: MigrationAssessmentVerifyBuildPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentVerifyBuildQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Verify Build
 */
export const prefetchMigrationAssessmentVerifyBuildQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: MigrationAssessmentVerifyBuildPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyBuild>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentVerifyBuildQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Verify Build
 */
export const invalidateMigrationAssessmentVerifyBuild = async (
  queryClient: QueryClient,
  { runId }: MigrationAssessmentVerifyBuildPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentVerifyBuildQueryKey({ runId }) }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment List Software Components
 */
export const migrationAssessmentListSoftwareComponents = (
  params?: MigrationAssessmentListSoftwareComponentsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSoftwareComponentsResponse>(
    { url: `/v1/migration-assessment/software-components`, method: 'GET', params, signal },
    options,
  );
};

export const getMigrationAssessmentListSoftwareComponentsQueryKey = (
  params?: MigrationAssessmentListSoftwareComponentsParams,
) => {
  return ['v1', 'migration-assessment', 'software-components', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentListSoftwareComponentsQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListSoftwareComponentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentListSoftwareComponentsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>> = ({ signal }) =>
    migrationAssessmentListSoftwareComponents(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentListSoftwareComponentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>
>;
export type MigrationAssessmentListSoftwareComponentsQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentListSoftwareComponents<
  TData = Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | MigrationAssessmentListSoftwareComponentsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListSoftwareComponents<
  TData = Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListSoftwareComponentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentListSoftwareComponents<
  TData = Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListSoftwareComponentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment List Software Components
 */

export function useMigrationAssessmentListSoftwareComponents<
  TData = Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentListSoftwareComponentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentListSoftwareComponentsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment List Software Components
 */
export const prefetchMigrationAssessmentListSoftwareComponentsQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: MigrationAssessmentListSoftwareComponentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentListSoftwareComponents>>, TError, TData>
    >;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentListSoftwareComponentsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment List Software Components
 */
export const invalidateMigrationAssessmentListSoftwareComponents = async (
  queryClient: QueryClient,
  params?: MigrationAssessmentListSoftwareComponentsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentListSoftwareComponentsQueryKey(params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Test Sink Channels
 */
export const migrationAssessmentTestSinkChannels = (
  params?: MigrationAssessmentTestSinkChannelsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTestSinkChannelsResponse>(
    { url: `/v1/migration-assessment/test-sink-channels`, method: 'GET', params, signal },
    options,
  );
};

export const getMigrationAssessmentTestSinkChannelsQueryKey = (params?: MigrationAssessmentTestSinkChannelsParams) => {
  return ['v1', 'migration-assessment', 'test-sink-channels', ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentTestSinkChannelsQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentTestSinkChannelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentTestSinkChannelsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>> = ({ signal }) =>
    migrationAssessmentTestSinkChannels(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentTestSinkChannelsQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>
>;
export type MigrationAssessmentTestSinkChannelsQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentTestSinkChannels<
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | MigrationAssessmentTestSinkChannelsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentTestSinkChannels<
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentTestSinkChannelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentTestSinkChannels<
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentTestSinkChannelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Test Sink Channels
 */

export function useMigrationAssessmentTestSinkChannels<
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MigrationAssessmentTestSinkChannelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentTestSinkChannelsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Test Sink Channels
 */
export const prefetchMigrationAssessmentTestSinkChannelsQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: MigrationAssessmentTestSinkChannelsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannels>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentTestSinkChannelsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Test Sink Channels
 */
export const invalidateMigrationAssessmentTestSinkChannels = async (
  queryClient: QueryClient,
  params?: MigrationAssessmentTestSinkChannelsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMigrationAssessmentTestSinkChannelsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Migration Assessment Test Sink Channel
 */
export const migrationAssessmentTestSinkChannel = (
  { channel }: MigrationAssessmentTestSinkChannelPathParameters,
  params?: MigrationAssessmentTestSinkChannelParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLTestSinkChannelResponse>(
    { url: `/v1/migration-assessment/test-sink/${channel}`, method: 'GET', params, signal },
    options,
  );
};

export const getMigrationAssessmentTestSinkChannelQueryKey = (
  { channel }: MigrationAssessmentTestSinkChannelPathParameters,
  params?: MigrationAssessmentTestSinkChannelParams,
) => {
  return ['v1', 'migration-assessment', 'test-sink', channel, ...(params ? [params] : [])] as const;
};

export const getMigrationAssessmentTestSinkChannelQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { channel }: MigrationAssessmentTestSinkChannelPathParameters,
  params?: MigrationAssessmentTestSinkChannelParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentTestSinkChannelQueryKey({ channel }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>> = ({ signal }) =>
    migrationAssessmentTestSinkChannel({ channel }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: channel !== null && channel !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentTestSinkChannelQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>
>;
export type MigrationAssessmentTestSinkChannelQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentTestSinkChannel<
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentTestSinkChannelPathParameters,
  params: undefined | MigrationAssessmentTestSinkChannelParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentTestSinkChannel<
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentTestSinkChannelPathParameters,
  params?: MigrationAssessmentTestSinkChannelParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentTestSinkChannel<
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentTestSinkChannelPathParameters,
  params?: MigrationAssessmentTestSinkChannelParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Test Sink Channel
 */

export function useMigrationAssessmentTestSinkChannel<
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { channel }: MigrationAssessmentTestSinkChannelPathParameters,
  params?: MigrationAssessmentTestSinkChannelParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentTestSinkChannelQueryOptions({ channel }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Test Sink Channel
 */
export const prefetchMigrationAssessmentTestSinkChannelQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { channel }: MigrationAssessmentTestSinkChannelPathParameters,
  params?: MigrationAssessmentTestSinkChannelParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentTestSinkChannel>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentTestSinkChannelQueryOptions({ channel }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Test Sink Channel
 */
export const invalidateMigrationAssessmentTestSinkChannel = async (
  queryClient: QueryClient,
  { channel }: MigrationAssessmentTestSinkChannelPathParameters,
  params?: MigrationAssessmentTestSinkChannelParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentTestSinkChannelQueryKey({ channel }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Verify Artifact
 */
export const migrationAssessmentVerifyArtifact = (
  tLVerifyArtifactRequest: BodyType<TLVerifyArtifactRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMigrationAssessmentProxyResponse>(
    {
      url: `/v1/migration-assessment/verify`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLVerifyArtifactRequest,
      signal,
    },
    options,
  );
};

export const getMigrationAssessmentVerifyArtifactQueryKey = (
  tLVerifyArtifactRequest?: BodyType<TLVerifyArtifactRequest>,
) => {
  return ['POST', 'v1', 'migration-assessment', 'verify', tLVerifyArtifactRequest] as const;
};

export const getMigrationAssessmentVerifyArtifactQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLVerifyArtifactRequest: BodyType<TLVerifyArtifactRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMigrationAssessmentVerifyArtifactQueryKey(tLVerifyArtifactRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>> = ({ signal }) =>
    migrationAssessmentVerifyArtifact(tLVerifyArtifactRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MigrationAssessmentVerifyArtifactQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>
>;
export type MigrationAssessmentVerifyArtifactQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentVerifyArtifact<
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLVerifyArtifactRequest: BodyType<TLVerifyArtifactRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentVerifyArtifact<
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLVerifyArtifactRequest: BodyType<TLVerifyArtifactRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentVerifyArtifact<
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLVerifyArtifactRequest: BodyType<TLVerifyArtifactRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Verify Artifact
 */

export function useMigrationAssessmentVerifyArtifact<
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLVerifyArtifactRequest: BodyType<TLVerifyArtifactRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentVerifyArtifactQueryOptions(tLVerifyArtifactRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Verify Artifact
 */
export const prefetchMigrationAssessmentVerifyArtifactQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLVerifyArtifactRequest: BodyType<TLVerifyArtifactRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentVerifyArtifact>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentVerifyArtifactQueryOptions(tLVerifyArtifactRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Verify Artifact
 */
export const invalidateMigrationAssessmentVerifyArtifact = async (
  queryClient: QueryClient,
  tLVerifyArtifactRequest: BodyType<TLVerifyArtifactRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentVerifyArtifactQueryKey(tLVerifyArtifactRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Build Integration
 */
export const migrationAssessmentBuildIntegration = (
  { workspaceId }: MigrationAssessmentBuildIntegrationPathParameters,
  tLImportWorkspaceRequestNull?: BodyType<TLImportWorkspaceRequest | null> | null,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMigrationAssessmentProxyResponse>(
    {
      url: `/v1/migration-assessment/workspaces/${workspaceId}/build`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLImportWorkspaceRequestNull,
      signal,
    },
    options,
  );
};

export const getMigrationAssessmentBuildIntegrationQueryKey = (
  { workspaceId }: MigrationAssessmentBuildIntegrationPathParameters,
  tLImportWorkspaceRequestNull?: BodyType<TLImportWorkspaceRequest | null>,
) => {
  return [
    'POST',
    'v1',
    'migration-assessment',
    'workspaces',
    workspaceId,
    'build',
    tLImportWorkspaceRequestNull,
  ] as const;
};

export const getMigrationAssessmentBuildIntegrationQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workspaceId }: MigrationAssessmentBuildIntegrationPathParameters,
  tLImportWorkspaceRequestNull?: BodyType<TLImportWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getMigrationAssessmentBuildIntegrationQueryKey({ workspaceId }, tLImportWorkspaceRequestNull);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>> = ({ signal }) =>
    migrationAssessmentBuildIntegration({ workspaceId }, tLImportWorkspaceRequestNull, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workspaceId !== null && workspaceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type MigrationAssessmentBuildIntegrationQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>
>;
export type MigrationAssessmentBuildIntegrationQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentBuildIntegration<
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentBuildIntegrationPathParameters,
  tLImportWorkspaceRequestNull: undefined | BodyType<TLImportWorkspaceRequest | null>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentBuildIntegration<
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentBuildIntegrationPathParameters,
  tLImportWorkspaceRequestNull?: BodyType<TLImportWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentBuildIntegration<
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentBuildIntegrationPathParameters,
  tLImportWorkspaceRequestNull?: BodyType<TLImportWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Build Integration
 */

export function useMigrationAssessmentBuildIntegration<
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workspaceId }: MigrationAssessmentBuildIntegrationPathParameters,
  tLImportWorkspaceRequestNull?: BodyType<TLImportWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentBuildIntegrationQueryOptions(
    { workspaceId },
    tLImportWorkspaceRequestNull,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Build Integration
 */
export const prefetchMigrationAssessmentBuildIntegrationQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workspaceId }: MigrationAssessmentBuildIntegrationPathParameters,
  tLImportWorkspaceRequestNull?: BodyType<TLImportWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentBuildIntegration>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentBuildIntegrationQueryOptions(
    { workspaceId },
    tLImportWorkspaceRequestNull,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Build Integration
 */
export const invalidateMigrationAssessmentBuildIntegration = async (
  queryClient: QueryClient,
  { workspaceId }: MigrationAssessmentBuildIntegrationPathParameters,
  tLImportWorkspaceRequestNull?: BodyType<TLImportWorkspaceRequest | null>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentBuildIntegrationQueryKey({ workspaceId }, tLImportWorkspaceRequestNull) },
    options,
  );

  return queryClient;
};

/**
 * @summary Migration Assessment Prepare Workspace
 */
export const migrationAssessmentPrepareWorkspace = (
  { workspaceId }: MigrationAssessmentPrepareWorkspacePathParameters,
  tLGenerateWorkspaceRequestNull?: BodyType<TLGenerateWorkspaceRequest | null> | null,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMigrationAssessmentProxyResponse>(
    {
      url: `/v1/migration-assessment/workspaces/${workspaceId}/prepare`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGenerateWorkspaceRequestNull,
      signal,
    },
    options,
  );
};

export const getMigrationAssessmentPrepareWorkspaceQueryKey = (
  { workspaceId }: MigrationAssessmentPrepareWorkspacePathParameters,
  tLGenerateWorkspaceRequestNull?: BodyType<TLGenerateWorkspaceRequest | null>,
) => {
  return [
    'POST',
    'v1',
    'migration-assessment',
    'workspaces',
    workspaceId,
    'prepare',
    tLGenerateWorkspaceRequestNull,
  ] as const;
};

export const getMigrationAssessmentPrepareWorkspaceQueryOptions = <
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workspaceId }: MigrationAssessmentPrepareWorkspacePathParameters,
  tLGenerateWorkspaceRequestNull?: BodyType<TLGenerateWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getMigrationAssessmentPrepareWorkspaceQueryKey({ workspaceId }, tLGenerateWorkspaceRequestNull);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>> = ({ signal }) =>
    migrationAssessmentPrepareWorkspace({ workspaceId }, tLGenerateWorkspaceRequestNull, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workspaceId !== null && workspaceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type MigrationAssessmentPrepareWorkspaceQueryResult = NonNullable<
  Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>
>;
export type MigrationAssessmentPrepareWorkspaceQueryError = ErrorType<TLHTTPValidationError>;

export function useMigrationAssessmentPrepareWorkspace<
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentPrepareWorkspacePathParameters,
  tLGenerateWorkspaceRequestNull: undefined | BodyType<TLGenerateWorkspaceRequest | null>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentPrepareWorkspace<
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentPrepareWorkspacePathParameters,
  tLGenerateWorkspaceRequestNull?: BodyType<TLGenerateWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>,
          TError,
          Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMigrationAssessmentPrepareWorkspace<
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: MigrationAssessmentPrepareWorkspacePathParameters,
  tLGenerateWorkspaceRequestNull?: BodyType<TLGenerateWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Migration Assessment Prepare Workspace
 */

export function useMigrationAssessmentPrepareWorkspace<
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workspaceId }: MigrationAssessmentPrepareWorkspacePathParameters,
  tLGenerateWorkspaceRequestNull?: BodyType<TLGenerateWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMigrationAssessmentPrepareWorkspaceQueryOptions(
    { workspaceId },
    tLGenerateWorkspaceRequestNull,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Migration Assessment Prepare Workspace
 */
export const prefetchMigrationAssessmentPrepareWorkspaceQuery = async <
  TData = Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workspaceId }: MigrationAssessmentPrepareWorkspacePathParameters,
  tLGenerateWorkspaceRequestNull?: BodyType<TLGenerateWorkspaceRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof migrationAssessmentPrepareWorkspace>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMigrationAssessmentPrepareWorkspaceQueryOptions(
    { workspaceId },
    tLGenerateWorkspaceRequestNull,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Migration Assessment Prepare Workspace
 */
export const invalidateMigrationAssessmentPrepareWorkspace = async (
  queryClient: QueryClient,
  { workspaceId }: MigrationAssessmentPrepareWorkspacePathParameters,
  tLGenerateWorkspaceRequestNull?: BodyType<TLGenerateWorkspaceRequest | null>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMigrationAssessmentPrepareWorkspaceQueryKey({ workspaceId }, tLGenerateWorkspaceRequestNull) },
    options,
  );

  return queryClient;
};
